import { Button } from 'antd';
import { Plus } from 'lucide-react';
import { Navigate, useNavigate, useParams, useLocation } from 'react-router';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { withinSection } from '../../../../../shared/components/section-trail';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { zoneViewPath } from '../../../zones.paths';
import { FlaggedAreaDrawer } from '../components/FlaggedAreaDrawer';
import { FlaggedAreasTable } from '../components/FlaggedAreasTable';
import { deactivateFlaggedArea, reactivateFlaggedArea, type FlaggedArea } from '../flagged-areas.api';
import { FLAGGED_AREAS_PATH, NEW_FLAGGED_AREA_PATH } from '../flagged-areas.paths';
import { useFlaggedAreaList } from '../hooks/use-flagged-area-list';

interface FlaggedAreasPageProps {
  /** Set on an area's routes: the table with its drawer open. */
  panel?: 'new' | 'area';
}

/** Zones → Flagged areas: where the office marks unsafe places and safe rest stops for the unsafe-stop alert. */
export function FlaggedAreasPage({ panel }: FlaggedAreasPageProps) {
  const areas = useFlaggedAreaList();
  const toast = useToast();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { id } = useParams();
  const openAreaId = panel === 'new' ? 'new' : id;

  if (areas.state.status === 'forbidden') return <Navigate to="/" replace />;

  async function setActive(area: FlaggedArea, isActive: boolean): Promise<void> {
    try {
      await (isActive ? reactivateFlaggedArea(area.id) : deactivateFlaggedArea(area.id));
    } catch {
      toast.error(`Couldn't ${isActive ? 'reactivate' : 'deactivate'} ${area.name}`);
      return;
    }
    await areas.reload();
    toast.success(`${area.name} ${isActive ? 'reactivated' : 'deactivated'}`);
  }

  return (
    <>
      <PageHeader
        breadcrumbs={withinSection('zones', { label: 'Flagged areas' })}
        title="Flagged areas"
        description="Mark where a stop is dangerous, and the rest places where an overnight stop is fine. The unsafe-stop alert reads them; they are not zones and are never watched by the GPS vendor."
        actions={
          <>
            <Button onClick={() => navigate(zoneViewPath('map'))}>Back to zones</Button>
            <Button type="primary" icon={<Plus className="size-4" />} onClick={() => navigate(`${NEW_FLAGGED_AREA_PATH}${search}`)}>
              New flagged area
            </Button>
          </>
        }
      />
      <FlaggedAreasTable areas={areas} onSetActive={setActive} />
      {openAreaId && (
        <FlaggedAreaDrawer key={openAreaId} areaId={openAreaId} onSaved={areas.reload} onClose={() => navigate(`${FLAGGED_AREAS_PATH}${search}`)} />
      )}
    </>
  );
}
